import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Settings as SettingsIcon } from 'lucide-react';
import { formatRelative } from '@custel/turkish';
import { api } from '../lib/api';
import { Button, Drawer } from './ui';
import { EmptyState } from './EmptyState';
import { Skeleton } from './Skeleton';
import { useSseEvents } from '../hooks/useSseEvents';
import { cn } from '../lib/cn';

/**
 * NotificationCenter — Sprint 28c.
 *
 * TopBar bell ikonuna tıklandığında sağdan açılan drawer. Kronolojik akış
 * (son 100), tek-tıkla okundu, "Tümünü okundu" CTA, "Ayarlar" linki.
 * SSE 'notification.created' eventinde otomatik refetch (canlı badge).
 */

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  payload: Record<string, unknown> | null;
  readAt: string | null;
  createdAt: string;
}

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Bildirim tip → görünür etiket eşlemesi artık modül scope'unda TR literal
 * tutmaz; render anında `t('notificationType.' + n.type)` ile `enum`
 * namespace'inden çözülür. Tip listesi anahtar üretimi için korunur.
 */
const NOTIFICATION_TYPES = [
  'receipt.reminder',
  'approval.created',
  'approval.decided',
  'budget.exceeded',
  'budget.warning',
  'card.frozen',
  'tx.authorized',
  'tx.declined',
  'card.topup',
  'card.limit_changed',
  'alarm.critical',
  'einvoice.received',
  'system.maintenance',
] as const;

export function NotificationCenter({ open, onClose }: NotificationCenterProps) {
  const { t } = useTranslation('enum');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.get<{ items: NotificationItem[] }>('/notifications'),
    enabled: open,
  });

  const markRead = useMutation({
    mutationFn: (id: string) => api.patch(`/notifications/${id}/read`, undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: () => api.post<{ updated: number }>('/notifications/read-all'),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });

  // SSE — canlı bildirim eklendiğinde listeyi tazele (drawer açıkken)
  const handleSse = useCallback(
    (event: { type: string }) => {
      if (event.type === 'notification.created') {
        void queryClient.invalidateQueries({ queryKey: ['notifications'] });
        void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    },
    [queryClient],
  );
  useSseEvents(handleSse);

  const items = query.data?.items ?? [];
  const unread = items.filter((n) => !n.readAt);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('notifications.title')}
      description={
        unread.length > 0 ? t('notifications.unreadCount', { count: unread.length }) : t('notifications.allRead')
      }
      size="sm"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
          <Link
            to="/ayarlar/bildirimler"
            onClick={onClose}
            className="inline-flex h-8 items-center gap-2 rounded-custel-lg border border-custel-border bg-custel-paper px-3 text-[13px] font-medium text-custel-ink transition-all hover:-translate-y-px hover:bg-custel-g100 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink"
          >
            <SettingsIcon size={14} aria-hidden="true" />
            {t('notifications.settings')}
          </Link>
          <Button
            variant="primary"
            size="sm"
            onClick={() => markAllRead.mutate()}
            disabled={unread.length === 0 || markAllRead.isPending}
            leadingIcon={<CheckCheck size={14} aria-hidden="true" />}
          >
            {t('notifications.markAllRead')}
          </Button>
        </>
      }
    >
      {query.isLoading && (
        <div className="space-y-3" aria-busy="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-custel-md border border-custel-border p-3">
              <Skeleton className="mb-2 h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mt-2 h-3 w-1/4" />
            </div>
          ))}
        </div>
      )}

      {!query.isLoading && items.length === 0 && (
        <EmptyState title={t('notifications.emptyTitle')} description={t('notifications.emptyDescription')} />
      )}

      {!query.isLoading && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((n) => (
            <NotificationRow key={n.id} n={n} onClick={() => !n.readAt && markRead.mutate(n.id)} />
          ))}
        </ul>
      )}
    </Drawer>
  );
}

function NotificationRow({ n, onClick }: { n: NotificationItem; onClick: () => void }) {
  const { t } = useTranslation('enum');
  const unread = !n.readAt;
  const typeLabel = NOTIFICATION_TYPES.includes(n.type as (typeof NOTIFICATION_TYPES)[number])
    ? t('notificationType.' + n.type)
    : n.type;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        aria-label={
          unread
            ? t('notifications.itemAriaUnread', { type: typeLabel })
            : t('notifications.itemAria', { type: typeLabel })
        }
        className={cn(
          'group relative block w-full rounded-custel-md border p-3 text-left transition-colors duration-custel-fast focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-ink',
          unread ? 'border-custel-border bg-custel-paper hover:bg-custel-g100' : 'border-custel-border bg-custel-krem',
        )}
      >
        {unread && (
          <span
            aria-hidden="true"
            className="absolute right-3 top-3 h-2 w-2 rounded-full bg-custel-lime"
            style={{ boxShadow: '0 0 0 3px rgba(228, 243, 35, 0.25)' }}
          />
        )}
        <div className="flex items-start gap-2.5 pr-6">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-center gap-2">
              <span className="font-mono text-[10px] font-medium uppercase tracking-[0.06em] text-custel-g500">
                {typeLabel}
              </span>
              <span className="font-mono text-[10px] text-custel-g500">{formatRelative(n.createdAt)}</span>
            </div>
            <p className={cn('text-sm leading-tight', unread ? 'font-medium text-custel-ink' : 'text-custel-g700')}>
              {n.title}
            </p>
            <p className="mt-0.5 text-[13px] leading-snug text-custel-g700">{n.body}</p>
          </div>
        </div>
        {unread && (
          <span className="mt-2 inline-flex items-center gap-1 text-[11px] text-custel-g500 opacity-0 transition-opacity group-hover:opacity-100">
            <Check size={11} aria-hidden="true" />
            {t('notifications.markRead')}
          </span>
        )}
      </button>
    </li>
  );
}

/**
 * Hook — TopBar Bell ikonu için unread count + badge.
 */
export function useUnreadNotificationCount() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['notifications-unread-count'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
  });

  // SSE 'notification.created' → invalidate
  const handler = useCallback(
    (event: { type: string }) => {
      if (event.type === 'notification.created') {
        void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      }
    },
    [queryClient],
  );
  useSseEvents(handler);

  // Bell ikonu için Bell named export'u tüketicide kullanır.
  return query.data?.count ?? 0;
}

// Re-export Bell — TopBar import için sembolik
export { Bell };
